/**
 * BulkAddItemConfig
 * Step 2 of the unified bulk add flow.
 *
 * Each selected item gets a card with type-specific lot entry fields.
 * Products: Select multiple variants → per-variant lot cards (Cases, Units, Lot#, Batch#, Mfg/Expiry Date).
 * Chemicals: Qty, Lot#, Batch#, Unit Cost, Expiry Date.
 * Generics:  Qty, Lot#, Batch#, Unit Cost.
 *
 * Shared vendor/invoice section appears at top ONLY when non-product items are selected.
 */

import React, { useState, useEffect } from 'react';
import { supabase } from '../../../../../../supabaseClient';
import {
  Box, Typography, TextField, Paper, Chip, Button, IconButton,
  Dialog, DialogTitle, DialogContent, InputAdornment, Grid,
  Table, TableHead, TableBody, TableRow, TableCell,
  useTheme, alpha, Divider, DialogActions
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Close as CloseIcon,
  Settings as SettingsIcon,
  Check as CheckIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';

// ── Constants ─────────────────────────────────────────────────────────────────

const TYPE_COLORS = {
  products:  '#1565c0',
  chemicals: '#7b1fa2',
  caps:      '#00796b',
  bottles:   '#2e7d32',
  labels:    '#e65100',
  supplies:  '#5d4037',
};

const TYPE_LABELS = {
  products:  'Product',
  chemicals: 'Chemical',
  caps:      'Cap',
  bottles:   'Bottle',
  labels:    'Label',
  supplies:  'Supply',
};

export const initLot = (type, item = null) => {
  const cost = (item && type !== 'products')
    ? String(item.unit_cost || item.cost || '')
    : '';
  if (type === 'products')
    return { palletQty: '', caseQty: '', unitQty: '', lotNumber: '', batchNumber: '', mfgDate: '', expiryDate: '' };
  if (type === 'chemicals')
    return { quantity: '', lotNumber: '', batchNumber: '', unitCost: cost, expiryDate: '' };
  return { quantity: '', lotNumber: '', batchNumber: '', unitCost: cost };
};

// ── Shared text field style ────────────────────────────────────────────────────

const tfSx = (theme) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: 2,
    backgroundColor: 'rgba(255,255,255,0.9)',
    backdropFilter: 'blur(10px)',
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: alpha(theme.palette.primary.main, 0.25)
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.primary.main
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.primary.main
    }
  },
  '& .MuiInputLabel-root': { fontWeight: 500 }
});

// ── Variant dialog (multi-select) ─────────────────────────────────────────────

function VariantDialog({ open, onClose, variants, search, onSearchChange, currentVariants, onConfirm }) {
  const theme = useTheme();
  const [localSelected, setLocalSelected] = useState([]);

  useEffect(() => {
    if (open) setLocalSelected(currentVariants || []);
  }, [open]);

  const parseSize = (name) => parseFloat((name || '').match(/[\d.]+/)?.[0] ?? 0);
  const filtered = variants
    .filter(v =>
      v.name?.toLowerCase().includes(search.toLowerCase()) ||
      (v.sku || '').toLowerCase().includes(search.toLowerCase())
    )
    .slice()
    .sort((a, b) => parseSize(b.name) - parseSize(a.name));

  const isSelected = (id) => localSelected.some(v => v.id === id);

  const toggle = (variant) => {
    if (isSelected(variant.id)) {
      setLocalSelected(prev => prev.filter(v => v.id !== variant.id));
    } else {
      setLocalSelected(prev => [...prev, { id: variant.id, name: variant.name, sku: variant.sku }]);
    }
  };

  const handleDone = () => {
    onConfirm(localSelected);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth
      PaperProps={{ sx: { borderRadius: 3, minHeight: '55vh' } }}
      sx={{ '& .MuiBackdrop-root': { backgroundColor: 'rgba(0,0,0,0.8)' } }}
    >
      <DialogTitle sx={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`
      }}>
        <Typography variant="h6" sx={{ fontWeight: 500 }}>Select Variants</Typography>
        <IconButton onClick={onClose}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 3 }}>
        <TextField
          fullWidth placeholder="Search variants..."
          value={search} onChange={e => onSearchChange(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
          sx={{ my: 2, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
        />
        <Table sx={{ '& .MuiTableCell-root': { fontSize: '0.95rem', py: 2 } }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 500 }}>Variant</TableCell>
              <TableCell sx={{ fontWeight: 500 }}>SKU</TableCell>
              <TableCell align="right" sx={{ fontWeight: 500 }}>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.map(v => {
              const selected = isSelected(v.id);
              return (
                <TableRow key={v.id} hover
                  sx={{ '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.04) } }}>
                  <TableCell sx={{ fontWeight: 500 }}>{v.name}</TableCell>
                  <TableCell sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>{v.sku || '—'}</TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      variant={selected ? 'contained' : 'outlined'}
                      color={selected ? 'primary' : 'inherit'}
                      startIcon={selected ? <CheckIcon sx={{ fontSize: 14 }} /> : null}
                      sx={{ borderRadius: 2, px: 3, minWidth: 100 }}
                      onClick={() => toggle(v)}
                    >
                      {selected ? 'Selected' : 'Select'}
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
            {filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  <Typography color="text.secondary" sx={{ py: 4 }}>No variants found</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${alpha(theme.palette.grey[300], 0.8)}` }}>
        <Button
          variant="contained"
          onClick={handleDone}
          sx={{ borderRadius: 2, px: 4, py: 1.25, fontWeight: 500, fontSize: '0.95rem' }}
        >
          Done{localSelected.length > 0 ? ` (${localSelected.length})` : ''}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Lot entry components ───────────────────────────────────────────────────────

function LotHeader({ index, total, onRemove }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
      <Typography variant="caption" sx={{ fontWeight: 500, color: 'text.secondary', fontSize: '0.78rem' }}>
        Lot Entry{total > 1 ? ` #${index + 1}` : ''}
      </Typography>
      {total > 1 && (
        <IconButton
          size="small"
          onClick={onRemove}
          sx={{
            color: 'text.secondary',
            '&:hover': { color: 'error.main', bgcolor: alpha('#d32f2f', 0.08) }
          }}
        >
          <DeleteIcon sx={{ fontSize: 17 }} />
        </IconButton>
      )}
    </Box>
  );
}

function ProductLotCard({ lot, index, total, onChange, onRemove, onOpenConfig }) {
  const theme = useTheme();
  const sx    = tfSx(theme);

  const hasConfig     = !!lot._configId;
  const enableCases   = lot._enableCases   ?? true;
  const enablePallets = lot._enablePallets ?? false;
  const unitsPerCase  = parseInt(lot._unitsPerCase)   || 1;
  const casesPerPallet = parseInt(lot._casesPerPallet) || 1;

  const pQty = parseInt(lot.palletQty) || 0;
  const cQty = parseInt(lot.caseQty)   || 0;
  const uQty = parseInt(lot.unitQty)   || 0;
  const totalBase = (pQty * casesPerPallet * unitsPerCase) + (cQty * unitsPerCase) + uQty;

  const shelfMonths = lot.mfgDate && lot.expiryDate
    ? Math.round((new Date(lot.expiryDate) - new Date(lot.mfgDate)) / (1000 * 60 * 60 * 24 * 30.44))
    : null;

  const set = (field, value) => onChange(index, { ...lot, [field]: value });

  return (
    <Box sx={{
      p: 2.5, mb: 2, borderRadius: 2,
      border: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
      bgcolor: alpha(theme.palette.grey[50], 0.5)
    }}>
      <LotHeader index={index} total={total} onRemove={onRemove} />

      {/* Unit Configuration */}
      <Box sx={{ mb: 2 }}>
        <Button
          fullWidth variant="outlined" size="small"
          onClick={() => onOpenConfig(index)}
          startIcon={<SettingsIcon sx={{ fontSize: 20 }} />}
          sx={{
            justifyContent: 'flex-start', height: 52, borderRadius: 2,
            backgroundColor: 'white',
            borderColor: hasConfig ? alpha(theme.palette.primary.main, 0.4) : alpha(theme.palette.grey[400], 0.8),
            color: hasConfig ? 'text.primary' : 'text.secondary',
            fontWeight: 500, px: 2.5, textTransform: 'none', fontSize: '0.95rem',
            '&:hover': { borderColor: theme.palette.primary.main }
          }}
        >
          {hasConfig
            ? `${lot._configName} — ${enableCases ? `${unitsPerCase} units/case` : 'Units only'}${enablePallets && enableCases ? ` · ${casesPerPallet} cases/pallet` : ''}`
            : 'Select Unit Configuration *'}
        </Button>
      </Box>

      {/* Quantities */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        {hasConfig && enablePallets && (
          <Grid item xs={4}>
            <TextField fullWidth size="small" label="Pallets" type="number"
              value={lot.palletQty || ''} onChange={e => set('palletQty', e.target.value)}
              inputProps={{ min: 0, step: 1 }} helperText={`= ${casesPerPallet} cases ea.`} sx={sx} />
          </Grid>
        )}
        {hasConfig && enableCases && (
          <Grid item xs={enablePallets ? 4 : 6}>
            <TextField fullWidth size="small" label="Cases" type="number"
              value={lot.caseQty || ''} onChange={e => set('caseQty', e.target.value)}
              inputProps={{ min: 0, step: 1 }} helperText={`= ${unitsPerCase} units ea.`} sx={sx} />
          </Grid>
        )}
        <Grid item xs={hasConfig && enablePallets ? 4 : (hasConfig && enableCases ? 6 : 12)}>
          <TextField fullWidth size="small" label="Units" type="number"
            value={lot.unitQty || ''} onChange={e => set('unitQty', e.target.value)}
            inputProps={{ min: 0, step: 1 }} sx={sx} />
        </Grid>
      </Grid>

      {totalBase > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 500, fontSize: '0.8rem' }}>
            = {totalBase.toLocaleString()} total base units
          </Typography>
        </Box>
      )}

      {/* Lot + Batch */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={6}>
          <TextField fullWidth size="small" label="Lot Number *"
            value={lot.lotNumber} onChange={e => set('lotNumber', e.target.value)}
            placeholder="Required" sx={sx} />
        </Grid>
        <Grid item xs={6}>
          <TextField fullWidth size="small" label="Batch Number *"
            value={lot.batchNumber} onChange={e => set('batchNumber', e.target.value)}
            placeholder="Required" sx={sx} />
        </Grid>
      </Grid>

      {/* Dates */}
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <DatePicker
              label="Mfg Date"
              value={lot.mfgDate ? dayjs(lot.mfgDate) : null}
              onChange={v => set('mfgDate', v ? v.format('YYYY-MM-DD') : '')}
              slotProps={{ textField: { size: 'small', fullWidth: true, sx } }}
            />
          </Grid>
          <Grid item xs={6}>
            <DatePicker
              label="Expiry Date"
              value={lot.expiryDate ? dayjs(lot.expiryDate) : null}
              onChange={v => set('expiryDate', v ? v.format('YYYY-MM-DD') : '')}
              slotProps={{ textField: { size: 'small', fullWidth: true, sx } }}
            />
          </Grid>
        </Grid>
      </LocalizationProvider>

      {shelfMonths !== null && shelfMonths >= 0 && (
        <Box sx={{ mt: 2 }}>
          <TextField
            fullWidth size="small" label="Shelf Life"
            value={`${shelfMonths} month${shelfMonths !== 1 ? 's' : ''}`}
            InputProps={{ readOnly: true }}
            helperText="Auto-calculated from Mfg & Expiry dates"
            sx={{ ...sx, '& .MuiInputBase-input': { color: 'success.main', fontWeight: 500 } }}
          />
        </Box>
      )}
    </Box>
  );
}

function ChemicalLotCard({ lot, index, total, item, onChange, onRemove }) {
  const theme       = useTheme();
  const sx          = tfSx(theme);
  const unitType    = item.primary_unit_type    || 'units';
  const unitMeasure = item.primary_unit_measure  || '';
  const unitQtyPer  = parseFloat(item.primary_unit_quantity) || null;
  const lotMode     = lot.quantityMode || 'container';
  const qtyNum      = parseFloat(lot.quantity) || 0;
  const calcTotal   = unitQtyPer && qtyNum > 0 && lotMode === 'container'
    ? (qtyNum * unitQtyPer).toFixed(3) : null;
  const calcContainers = unitQtyPer && qtyNum > 0 && lotMode === 'measure'
    ? (qtyNum / unitQtyPer).toFixed(3) : null;

  const set = (field, value) => onChange(index, { ...lot, [field]: value });

  return (
    <Box sx={{
      p: 2.5, mb: 2, borderRadius: 2,
      border: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
      bgcolor: alpha(theme.palette.grey[50], 0.5)
    }}>
      <LotHeader index={index} total={total} onRemove={onRemove} />

      {/* Container / Measure toggle */}
      {unitQtyPer && unitMeasure && (
        <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
          <Button size="small"
            variant={lotMode === 'container' ? 'contained' : 'outlined'}
            onClick={() => set('quantityMode', 'container')}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 500 }}>
            Enter by {unitType}
          </Button>
          <Button size="small"
            variant={lotMode === 'measure' ? 'contained' : 'outlined'}
            onClick={() => set('quantityMode', 'measure')}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 500 }}>
            Enter by {unitMeasure}
          </Button>
        </Box>
      )}

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={6}>
          <TextField fullWidth size="small"
            label={lotMode === 'measure' ? `Quantity * (${unitMeasure})` : `Quantity * (${unitType})`}
            type="number"
            value={lot.quantity}
            onChange={e => set('quantity', e.target.value)}
            inputProps={{ min: 0, step: '0.0001' }}
            helperText={unitQtyPer ? `1 ${unitType} = ${unitQtyPer}${unitMeasure}` : undefined}
            sx={sx} />
        </Grid>
        {(calcTotal || calcContainers) && (
          <Grid item xs={6}>
            <TextField fullWidth size="small"
              label={lotMode === 'measure' ? `Containers (${unitType})` : `Total (${unitMeasure})`}
              value={lotMode === 'measure' ? calcContainers : calcTotal}
              InputProps={{ readOnly: true }}
              helperText="Auto-calculated"
              sx={{ ...sx, '& .MuiInputBase-input': { color: 'success.main', fontWeight: 500 } }} />
          </Grid>
        )}
      </Grid>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={6}>
          <TextField fullWidth size="small" label="Lot Number *"
            value={lot.lotNumber} onChange={e => set('lotNumber', e.target.value)}
            placeholder="Required" sx={sx} />
        </Grid>
        <Grid item xs={6}>
          <TextField fullWidth size="small" label="Batch Number *"
            value={lot.batchNumber} onChange={e => set('batchNumber', e.target.value)}
            placeholder="Required" sx={sx} />
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={6}>
          <TextField fullWidth size="small" label="Unit Cost *" type="number"
            value={lot.unitCost}
            onChange={e => set('unitCost', e.target.value)}
            inputProps={{ min: 0, step: '0.01' }}
            placeholder="Required"
            sx={sx} />
        </Grid>
        <Grid item xs={6}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Expiry Date"
              value={lot.expiryDate ? dayjs(lot.expiryDate) : null}
              onChange={v => set('expiryDate', v ? v.format('YYYY-MM-DD') : '')}
              slotProps={{ textField: { size: 'small', fullWidth: true, sx } }}
            />
          </LocalizationProvider>
        </Grid>
      </Grid>
    </Box>
  );
}

function GenericLotCard({ lot, index, total, onChange, onRemove }) {
  const theme = useTheme();
  const sx    = tfSx(theme);
  const set   = (field, value) => onChange(index, { ...lot, [field]: value });

  return (
    <Box sx={{
      p: 2.5, mb: 2, borderRadius: 2,
      border: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
      bgcolor: alpha(theme.palette.grey[50], 0.5)
    }}>
      <LotHeader index={index} total={total} onRemove={onRemove} />

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={6}>
          <TextField fullWidth size="small" label="Quantity *" type="number"
            value={lot.quantity} onChange={e => set('quantity', e.target.value)}
            inputProps={{ min: 0, step: 1 }} placeholder="Required" sx={sx} />
        </Grid>
        <Grid item xs={6}>
          <TextField fullWidth size="small" label="Unit Cost *" type="number"
            value={lot.unitCost} onChange={e => set('unitCost', e.target.value)}
            inputProps={{ min: 0, step: '0.01' }} placeholder="Required" sx={sx} />
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={6}>
          <TextField fullWidth size="small" label="Lot Number *"
            value={lot.lotNumber} onChange={e => set('lotNumber', e.target.value)}
            placeholder="Required" sx={sx} />
        </Grid>
        <Grid item xs={6}>
          <TextField fullWidth size="small" label="Batch Number *"
            value={lot.batchNumber} onChange={e => set('batchNumber', e.target.value)}
            placeholder="Required" sx={sx} />
        </Grid>
      </Grid>
    </Box>
  );
}

// ── Vendor dialog ─────────────────────────────────────────────────────────────

function VendorDialog({ open, onClose, suppliers, onSelect }) {
  const theme = useTheme();
  const [search, setSearch] = useState('');
  const filtered = suppliers.filter(s =>
    s.name?.toLowerCase().includes(search.toLowerCase())
  );
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: 3, minHeight: '50vh' } }}
      sx={{ '& .MuiBackdrop-root': { backgroundColor: 'rgba(0,0,0,0.8)' } }}
    >
      <DialogTitle sx={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`
      }}>
        <Typography variant="h6" sx={{ fontWeight: 500 }}>Select Vendor</Typography>
        <IconButton onClick={onClose}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 3 }}>
        <TextField
          fullWidth placeholder="Search vendors..."
          value={search} onChange={e => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
          sx={{ my: 2, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
        />
        <Table sx={{ '& .MuiTableCell-root': { fontSize: '0.95rem', py: 2 } }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 500 }}>Vendor Name</TableCell>
              <TableCell align="right" sx={{ fontWeight: 500 }}>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.map(s => (
              <TableRow key={s.id} hover
                sx={{ '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.04) } }}>
                <TableCell>{s.name}</TableCell>
                <TableCell align="right">
                  <Button size="small" variant="outlined" sx={{ borderRadius: 2, px: 3 }}
                    onClick={() => { onSelect(s); onClose(); setSearch(''); }}>
                    Select
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={2} align="center">
                  <Typography color="text.secondary" sx={{ py: 4 }}>No vendors found</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </DialogContent>
    </Dialog>
  );
}

// ── Unit Config dialog ────────────────────────────────────────────────────────

function UnitConfigDialog({ open, onClose, configs, search, onSearchChange, onSelect }) {
  const theme = useTheme();
  const filtered = configs.filter(c => c.name?.toLowerCase().includes(search.toLowerCase()));
  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth
      PaperProps={{ sx: { borderRadius: 3, minHeight: '55vh' } }}
      sx={{ '& .MuiBackdrop-root': { backgroundColor: 'rgba(0,0,0,0.8)' } }}
    >
      <DialogTitle sx={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`
      }}>
        <Typography variant="h6" sx={{ fontWeight: 500 }}>Select Unit Configuration</Typography>
        <IconButton onClick={onClose}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 3 }}>
        <TextField
          fullWidth placeholder="Search configurations..."
          value={search} onChange={e => onSearchChange(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
          sx={{ my: 2, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
        />
        <Table sx={{ '& .MuiTableCell-root': { fontSize: '0.95rem', py: 2 } }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 500 }}>Configuration</TableCell>
              <TableCell sx={{ fontWeight: 500 }}>Details</TableCell>
              <TableCell align="right" sx={{ fontWeight: 500 }}>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.map(c => (
              <TableRow key={c.id} hover
                sx={{ '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.04) } }}>
                <TableCell sx={{ fontWeight: 500 }}>{c.name}</TableCell>
                <TableCell sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>
                  {c.enable_cases !== false ? `${c.units_per_case || 1} units/case` : 'Units only'}
                  {c.enable_pallets && c.enable_cases !== false ? ` · ${c.cases_per_pallet || 1} cases/pallet` : ''}
                </TableCell>
                <TableCell align="right">
                  <Button size="small" variant="outlined" sx={{ borderRadius: 2, px: 3 }}
                    onClick={() => { onSelect(c); onClose(); }}>
                    Select
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  <Typography color="text.secondary" sx={{ py: 4 }}>No configurations found</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </DialogContent>
    </Dialog>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

export default function BulkAddItemConfig({
  companyId,
  selectedItems,
  itemsConfig,
  onItemsConfigChange,
  shared,
  onSharedChange,
  suppliers
}) {
  const theme = useTheme();
  const [vendorOpen,        setVendorOpen]        = useState(false);
  const [configDialogOpen,  setConfigDialogOpen]  = useState(false);
  const [configTarget,      setConfigTarget]      = useState(null); // { key, variantIdx, lotIdx }
  const [availableConfigs,  setAvailableConfigs]  = useState([]);
  const [configSearch,      setConfigSearch]      = useState('');
  const [variantDialogOpen, setVariantDialogOpen] = useState(false);
  const [variantDialogKey,  setVariantDialogKey]  = useState(null);
  const [variantItems,      setVariantItems]      = useState([]);
  const [variantSearch,     setVariantSearch]     = useState('');
  const sx = tfSx(theme);

  const hasNonProducts = selectedItems.some(i => i.inventoryType !== 'products');

  // ── Unit Config dialog ──────────────────────────────────────────────────────

  const openConfigDialog = async (key, variantIdx, lotIdx) => {
    setConfigTarget({ key, variantIdx, lotIdx });
    setConfigSearch('');
    setConfigDialogOpen(true);
    const { data } = await supabase
      .from('unit_configurations')
      .select('*')
      .eq('company_id', companyId)
      .eq('is_active', true)
      .order('name');
    setAvailableConfigs(data || []);
  };

  const handleConfigSelect = (config) => {
    const { key, variantIdx, lotIdx } = configTarget;
    const cfgState = itemsConfig[key] || { variants: [] };
    const variants = (cfgState.variants || []).map((v, vi) => {
      if (vi !== variantIdx) return v;
      const lots = (v.lots || []).map((l, i) => i === lotIdx ? {
        ...l,
        _configId:       config.id,
        _configName:     config.name,
        _unitsPerCase:   config.units_per_case   || 1,
        _casesPerPallet: config.cases_per_pallet || 1,
        _enableCases:    config.enable_cases   ?? true,
        _enablePallets:  config.enable_pallets ?? false
      } : l);
      return { ...v, lots };
    });
    onItemsConfigChange({ ...itemsConfig, [key]: { ...cfgState, variants } });
    setConfigDialogOpen(false);
    setConfigTarget(null);
  };

  // ── Variant dialog ──────────────────────────────────────────────────────────

  const openVariantDialog = async (key, productId) => {
    setVariantDialogKey(key);
    setVariantSearch('');
    setVariantDialogOpen(true);
    const { data } = await supabase
      .from('product_variants')
      .select('id, name, sku')
      .eq('product_id', productId)
      .eq('is_active', true)
      .order('name');
    setVariantItems(data || []);
  };

  const handleVariantsConfirm = (selectedVariants) => {
    const cfg = itemsConfig[variantDialogKey] || { variants: [] };
    const currentVariants = cfg.variants || [];
    // Preserve existing lots for variants that were already selected
    const newVariants = selectedVariants.map(v => {
      const existing = currentVariants.find(cv => cv.id === v.id);
      return existing || { id: v.id, name: v.name, sku: v.sku, lots: [initLot('products')] };
    });
    onItemsConfigChange({ ...itemsConfig, [variantDialogKey]: { ...cfg, variants: newVariants } });
    setVariantDialogKey(null);
  };

  // ── Per-variant lot helpers ─────────────────────────────────────────────────

  const updateVariantLot = (key, variantIdx, lotIdx, updated) => {
    const cfg = itemsConfig[key] || { variants: [] };
    const variants = (cfg.variants || []).map((v, vi) => {
      if (vi !== variantIdx) return v;
      const lots = (v.lots || []).map((l, i) => i === lotIdx ? updated : l);
      return { ...v, lots };
    });
    onItemsConfigChange({ ...itemsConfig, [key]: { ...cfg, variants } });
  };

  const addVariantLot = (key, variantIdx) => {
    const cfg = itemsConfig[key] || { variants: [] };
    const variants = (cfg.variants || []).map((v, vi) => {
      if (vi !== variantIdx) return v;
      return { ...v, lots: [...(v.lots || []), initLot('products')] };
    });
    onItemsConfigChange({ ...itemsConfig, [key]: { ...cfg, variants } });
  };

  const removeVariantLot = (key, variantIdx, lotIdx) => {
    const cfg = itemsConfig[key] || { variants: [] };
    const variants = (cfg.variants || []).map((v, vi) => {
      if (vi !== variantIdx) return v;
      const lots = (v.lots || []).filter((_, i) => i !== lotIdx);
      return { ...v, lots };
    });
    onItemsConfigChange({ ...itemsConfig, [key]: { ...cfg, variants } });
  };

  // ── Non-product lot helpers ─────────────────────────────────────────────────

  const updateLot = (key, lotIdx, updated) => {
    const cfg  = itemsConfig[key] || { lots: [] };
    const lots = cfg.lots.map((l, i) => i === lotIdx ? updated : l);
    onItemsConfigChange({ ...itemsConfig, [key]: { ...cfg, lots } });
  };

  const addLot = (key, type, item) => {
    const cfg = itemsConfig[key] || { lots: [] };
    onItemsConfigChange({
      ...itemsConfig,
      [key]: { ...cfg, lots: [...cfg.lots, initLot(type, item)] }
    });
  };

  const removeLot = (key, lotIdx) => {
    const cfg  = itemsConfig[key];
    const lots = cfg.lots.filter((_, i) => i !== lotIdx);
    onItemsConfigChange({ ...itemsConfig, [key]: { ...cfg, lots } });
  };

  return (
    <Box>
      {/* ── Shared vendor/invoice section (non-products only) ───────────── */}
      {hasNonProducts && (
        <Paper elevation={0} sx={{
          p: 2.5, mb: 3.5, borderRadius: 2,
          border: `1px solid ${alpha(theme.palette.grey[400], 0.35)}`,
          bgcolor: alpha(theme.palette.grey[50], 0.8)
        }}>
          <Typography variant="subtitle2" sx={{
            mb: 2, fontWeight: 500, color: 'text.secondary',
            fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.6px'
          }}>
            Vendor & Invoice — optional, applies to chemicals & packaging
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth size="small" label="Vendor"
                value={shared.vendor?.name || ''}
                onClick={() => setVendorOpen(true)}
                placeholder="Select vendor..."
                InputProps={{ readOnly: true, sx: { cursor: 'pointer' } }}
                inputProps={{ style: { cursor: 'pointer' } }}
                sx={{ ...sx, cursor: 'pointer' }}
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField fullWidth size="small" label="Invoice #"
                value={shared.invoiceNumber}
                onChange={e => onSharedChange({ ...shared, invoiceNumber: e.target.value })}
                sx={sx} />
            </Grid>
            <Grid item xs={6} md={2}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="Invoice Date"
                  value={shared.invoiceDate ? dayjs(shared.invoiceDate) : null}
                  onChange={v => onSharedChange({ ...shared, invoiceDate: v ? v.format('YYYY-MM-DD') : '' })}
                  slotProps={{ textField: { size: 'small', fullWidth: true, sx } }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField fullWidth size="small" label="PO #"
                value={shared.poNumber}
                onChange={e => onSharedChange({ ...shared, poNumber: e.target.value })}
                sx={sx} />
            </Grid>
            <Grid item xs={6} md={2}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="PO Date"
                  value={shared.poDate ? dayjs(shared.poDate) : null}
                  onChange={v => onSharedChange({ ...shared, poDate: v ? v.format('YYYY-MM-DD') : '' })}
                  slotProps={{ textField: { size: 'small', fullWidth: true, sx } }}
                />
              </LocalizationProvider>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* ── Per-item cards ──────────────────────────────────────────────── */}
      {selectedItems.map(item => {
        const key     = `${item.inventoryType}:${item.id}`;
        const cfg     = itemsConfig[key] || { lots: [], variants: [] };
        const lots    = cfg.lots || [];
        const variants = cfg.variants || [];
        const color   = TYPE_COLORS[item.inventoryType] || '#666';
        const isProduct = item.inventoryType === 'products';

        // Total lots across all variants (products) or flat lots (others)
        const totalLotCount = isProduct
          ? variants.reduce((s, v) => s + (v.lots?.length || 0), 0)
          : lots.length;

        return (
          <Paper key={key} elevation={0} sx={{
            mb: 3, borderRadius: 2,
            border: `1px solid ${alpha(color, 0.25)}`,
            overflow: 'hidden'
          }}>
            {/* Card header */}
            <Box sx={{
              px: 2.5, py: 1.75,
              bgcolor: alpha(color, 0.06),
              borderBottom: `1px solid ${alpha(color, 0.12)}`,
              display: 'flex', alignItems: 'center', gap: 1.5
            }}>
              <Chip
                label={TYPE_LABELS[item.inventoryType]}
                size="small"
                sx={{
                  height: 22, fontSize: '0.7rem',
                  bgcolor: alpha(color, 0.15), color,
                  fontWeight: 500,
                  '& .MuiChip-label': { px: 1 }
                }}
              />
              <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                {item.name}
              </Typography>
              {item.sku && (
                <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                  SKU: {item.sku}
                </Typography>
              )}
              {item.inventoryType === 'chemicals' && item.primary_unit_type && (
                <Typography variant="caption" sx={{ ml: 'auto', color: '#7b1fa2', fontWeight: 500 }}>
                  {item.primary_unit_quantity || '—'}{item.primary_unit_measure || ''} / {item.primary_unit_type}
                </Typography>
              )}
              {isProduct ? (
                <Chip
                  label={`${variants.length} variant${variants.length !== 1 ? 's' : ''} · ${totalLotCount} lot${totalLotCount !== 1 ? 's' : ''}`}
                  size="small"
                  sx={{
                    ml: 'auto', height: 20, fontSize: '0.65rem',
                    bgcolor: alpha(color, 0.1), color,
                    '& .MuiChip-label': { px: 0.8 }
                  }}
                />
              ) : (
                <Chip
                  label={`${totalLotCount} lot${totalLotCount !== 1 ? 's' : ''}`}
                  size="small"
                  sx={{
                    ml: item.inventoryType === 'chemicals' ? 0 : 'auto',
                    height: 20, fontSize: '0.65rem',
                    bgcolor: alpha(color, 0.1), color,
                    '& .MuiChip-label': { px: 0.8 }
                  }}
                />
              )}
            </Box>

            {/* Card body */}
            <Box sx={{ p: 2.5 }}>
              {isProduct ? (
                <>
                  {/* Variant selector button */}
                  <Box sx={{ mb: 2.5 }}>
                    <Button
                      fullWidth variant="outlined"
                      onClick={() => openVariantDialog(key, item.id)}
                      sx={{
                        justifyContent: 'flex-start', height: 52, borderRadius: 2,
                        backgroundColor: 'white',
                        borderColor: variants.length > 0 ? alpha(theme.palette.primary.main, 0.4) : alpha(theme.palette.grey[400], 0.8),
                        color: variants.length > 0 ? 'text.primary' : 'text.secondary',
                        fontWeight: 500, px: 2.5, textTransform: 'none', fontSize: '0.95rem',
                        '&:hover': { borderColor: theme.palette.primary.main }
                      }}
                    >
                      {variants.length > 0
                        ? `${variants.length} variant${variants.length !== 1 ? 's' : ''} selected: ${variants.map(v => v.name).join(', ')}`
                        : 'Select Variants *'}
                    </Button>
                  </Box>

                  {/* Per-variant lot sections */}
                  {variants.map((variant, variantIdx) => (
                    <Box key={variant.id}>
                      {variantIdx > 0 && <Divider sx={{ my: 2.5 }} />}
                      <Typography variant="subtitle2" sx={{
                        mb: 1.5, fontWeight: 500, color,
                        fontSize: '0.85rem'
                      }}>
                        {variant.name}
                      </Typography>
                      {(variant.lots || []).map((lot, lotIdx) => (
                        <ProductLotCard
                          key={lotIdx}
                          lot={lot}
                          index={lotIdx}
                          total={variant.lots.length}
                          onChange={(idx, updated) => updateVariantLot(key, variantIdx, idx, updated)}
                          onRemove={() => removeVariantLot(key, variantIdx, lotIdx)}
                          onOpenConfig={(li) => openConfigDialog(key, variantIdx, li)}
                        />
                      ))}
                      <Button
                        size="small"
                        startIcon={<AddIcon sx={{ fontSize: 15 }} />}
                        onClick={() => addVariantLot(key, variantIdx)}
                        sx={{
                          mt: 0.5, color, fontWeight: 500, fontSize: '0.82rem',
                          '&:hover': { bgcolor: alpha(color, 0.08) }
                        }}
                      >
                        Add Another Lot
                      </Button>
                    </Box>
                  ))}
                </>
              ) : (
                <>
                  {lots.map((lot, lotIdx) => {
                    const onChange = (idx, updated) => updateLot(key, idx, updated);
                    const onRemove = () => removeLot(key, lotIdx);

                    if (item.inventoryType === 'chemicals') {
                      return (
                        <ChemicalLotCard key={lotIdx} lot={lot} index={lotIdx}
                          total={lots.length} item={item} onChange={onChange} onRemove={onRemove} />
                      );
                    }
                    return (
                      <GenericLotCard key={lotIdx} lot={lot} index={lotIdx}
                        total={lots.length} onChange={onChange} onRemove={onRemove} />
                    );
                  })}

                  <Button
                    size="small"
                    startIcon={<AddIcon sx={{ fontSize: 15 }} />}
                    onClick={() => addLot(key, item.inventoryType, item)}
                    sx={{
                      mt: 0.5, color, fontWeight: 500, fontSize: '0.82rem',
                      '&:hover': { bgcolor: alpha(color, 0.08) }
                    }}
                  >
                    Add Another Lot
                  </Button>
                </>
              )}
            </Box>
          </Paper>
        );
      })}

      <VendorDialog
        open={vendorOpen}
        onClose={() => setVendorOpen(false)}
        suppliers={suppliers}
        onSelect={s => onSharedChange({ ...shared, vendor: s })}
      />

      <VariantDialog
        open={variantDialogOpen}
        onClose={() => { setVariantDialogOpen(false); setVariantDialogKey(null); }}
        variants={variantItems}
        search={variantSearch}
        onSearchChange={setVariantSearch}
        currentVariants={variantDialogKey ? (itemsConfig[variantDialogKey]?.variants || []) : []}
        onConfirm={handleVariantsConfirm}
      />

      <UnitConfigDialog
        open={configDialogOpen}
        onClose={() => { setConfigDialogOpen(false); setConfigTarget(null); }}
        configs={availableConfigs}
        search={configSearch}
        onSearchChange={setConfigSearch}
        onSelect={handleConfigSelect}
      />
    </Box>
  );
}
